import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback,formatMoneyCUSTOM } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.variantChanged.bind(this));
    const optionsLabels = this.querySelectorAll('.variant-option__button-label:not([data-location-custom-label])');
    optionsLabels.forEach(label => {
      label.addEventListener('click',function(event) {
        this.classList.remove('location-selected');
        let allLabels = label.closest('fieldset').querySelectorAll('label[data-location-custom-label]');
        allLabels.forEach(lbl => {
          lbl.classList.remove('active');
        });
        if(document.querySelector('[data-custom-sku-input]')){
          document.querySelector('[data-custom-sku-input]').removeAttribute('name');
          document.querySelector('[data-custom-sku-input]').value = "";
          document.querySelector('[data-custom-sku-input-property]').value = "";
          document.querySelector('[data-custom-sku-input-property]').removeAttribute('name');
        }
        // replace to original price
        let priceElement = label.closest('.product-information')?.querySelector('product-price [ref="priceContainer"] .price');
        if(priceElement) priceElement.innerHTML = formatMoneyCUSTOM(Number(priceElement.dataset.price),"${{ amount }}");
        let unitpriceElement = label.closest('.product-information')?.querySelector('product-price [ref="priceContainer"] .unit-price');
        if(unitpriceElement) unitpriceElement.innerHTML = `${unitpriceElement.dataset.price}`;
      });
    });

    const locationCustomLabels = this.querySelectorAll('[data-location-custom-label]');
    locationCustomLabels.forEach(label => {
      label.addEventListener('click', this.locationCustomLabelClicked.bind(this));
    });
    this.initOpenDescFormButton();

  }

  locationCustomLabelClicked(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const label = event.target.closest('label');
    const skuValue = label.dataset.skuValue;
    // remove active class from all labels
    let allLabels = label.closest('fieldset').querySelectorAll('label[data-location-custom-label]');
    allLabels.forEach(lbl => {
      lbl.classList.remove('active');
    });
    label.classList.add('active');
    if(document.querySelector('[data-custom-sku-input]')){
      document.querySelector('[data-custom-sku-input]').value = skuValue;
      document.querySelector('[data-custom-sku-input]').setAttribute('name', 'properties[_custom-sku]');
      document.querySelector('[data-custom-sku-input-property]').value = label.dataset.value;
      document.querySelector('[data-custom-sku-input-property]').setAttribute('name', 'properties[Variant]');
      
    }
    // add location-selected class to normal labels
    let normalLabels = label.closest('fieldset').querySelectorAll('label:not([data-location-custom-label])');
    normalLabels.forEach(label => {
      label.classList.add('location-selected');
    });
    // replace price
    let metafieldPrice = label.dataset.metafieldPrice;
    if(metafieldPrice) {
      let priceElement = label.closest('.product-information').querySelector('product-price [ref="priceContainer"] .price');
      if(priceElement) priceElement.innerHTML = formatMoneyCUSTOM(metafieldPrice,"${{ amount }}");

      let unitPrice = parseFloat(metafieldPrice) / parseFloat(label.dataset.unit);
      let unitpriceElement = label.closest('.product-information').querySelector('product-price [ref="priceContainer"] .unit-price');
      if(unitpriceElement) unitpriceElement.innerHTML = `/ ${formatMoneyCUSTOM(unitPrice,"${{ amount }}")} ${unitpriceElement.dataset.measurement}`;
    }

  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    // Get the variant description from the metafield
    this.updateDescription(selectedOption);

    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  updateDescription(selectedOption) {
    const descriptionElement = document.querySelector('[data-description-ele]');
    const proDescription = document.querySelector('[data-description-noscript]');
    if(descriptionElement) {
      descriptionElement.innerHTML = window.productVariantDescriptions[selectedOption.dataset.variantId] ? window.productVariantDescriptions[selectedOption.dataset.variantId] : proDescription?.innerHTML;
      this.initOpenDescFormButton();
    }
  }

  initOpenDescFormButton() {
    const openDescFormButton = document.querySelector('.open-desc-form');
    if(openDescFormButton) {
      openDescFormButton.addEventListener('click', function(e){
        e.preventDefault();
        document.querySelector('.open-desc-form-button')?.click();
      });
    }
  }
  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}